import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageLayout from '../components/PageLayout';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const features = [
    "Quality craftsmanship in every project",
    "Experienced and dedicated team",
    "Customer satisfaction guaranteed",
    "Innovative design solutions",
    "Transparent communication",
    "Timely project completion"
  ];

  return (
    <PageLayout
      title="About Us"
      subtitle="Building excellence with passion and precision"
    >
      <Helmet>
        <title>About Us | Custom Home Builders in Kannur, Kerala</title>
        <meta name="description" content="Learn about Master Home, the top construction and custom home building company in Kannur, Kerala. Building excellence with passion and precision since inception." />
        <link rel="canonical" href="https://masterhome.co.in/about" />
      </Helmet>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gradient">Our Story</h2>
          <p className="text-gray-600 leading-relaxed">
            Master Home has been a leading name in home construction, renovation,
            and custom home building across Kannur, Iritty, and Mattannur. Our journey began with
            a clear mission: to transform living spaces into extraordinary homes that reflect our
            clients' dreams and aspirations.
          </p>
          <p className="text-gray-600 leading-relaxed">
            What sets Master Home apart is our unwavering commitment to quality craftsmanship, attention
            to detail, and an exceptional focus on customer satisfaction. As one of the best builders in
            Kannur, we combine innovation with expertise to deliver remarkable results. From luxury home
            designs to eco-friendly building practices, each project we undertake is a testament to our passion
            for excellence and dedication to creating spaces that inspire comfort, style, and functionality.
          </p>
          <p className="text-gray-600 leading-relaxed">
            At Master Home, we don't just build houses — we create homes that tell your story.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="relative"
        >
          <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl transform rotate-2 opacity-10"></div>
          <img
            src="./about-image.jpg"
            alt="Our team at work"
            className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
          />
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:order-2"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gradient">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 p-4 glass-card"
              >
                <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:order-1 space-y-6"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gradient">Our Vision</h2>
          <p className="text-gray-600 leading-relaxed">
            We envision being the leading force in transforming the construction industry
            through innovation, sustainability, and unparalleled customer service. Our goal
            is to create homes that not only meet but exceed our clients' expectations.
          </p>
          <div className="pt-4">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all shadow-md hover:shadow-lg group"
            >
              Start Your Project
              <ArrowRight className="ml-2 h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>
      </div>

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </PageLayout>
  );
};

export default About;
